import { Children, isValidElement, type ReactNode } from "react";

const SCROLLABLE_TABLE_MIN_COLUMN_COUNT = 5;
const DOMINANT_COLUMN_MIN_AVERAGE_LENGTH = 48;
const DOMINANT_COLUMN_RATIO = 1.6;
const COMPACT_COLUMN_MAX_AVERAGE_LENGTH = 72;

type TableColumnMetrics = {
  index: number;
  averageLength: number;
  maxLength: number;
};

export type ScoutTableLayoutPlan = {
  columnCount: number;
  shouldScrollHorizontally: boolean;
  dominantColumnIndex: number | undefined;
  compactColumnIndexes: number[];
};

function normalizeCellText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function getNodeText(node?: ReactNode): string {
  let text = "";

  Children.forEach(node, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += `${child} `;
      return;
    }

    if (isValidElement<{ children?: ReactNode }>(child)) {
      text += getNodeText(child.props.children);
    }
  });

  return normalizeCellText(text);
}

function getTableRows(children?: ReactNode): string[][] {
  const rows: string[][] = [];

  const visit = (node?: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement<{ children?: ReactNode }>(child)) {
        return;
      }

      if (child.type === "tr") {
        const row: string[] = [];

        Children.forEach(child.props.children, (cell) => {
          if (!isValidElement<{ children?: ReactNode }>(cell)) {
            return;
          }

          row.push(getNodeText(cell.props.children));
        });

        rows.push(row);
        return;
      }

      visit(child.props.children);
    });
  };

  visit(children);

  return rows;
}

function getColumnMetrics(rows: string[][]): TableColumnMetrics[] {
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);

  return Array.from({ length: columnCount }, (_, index) => {
    const cells = rows.map((row) => row[index] ?? "").filter((cell) => cell.length > 0);
    const totalLength = cells.reduce((sum, cell) => sum + cell.length, 0);
    const averageLength = cells.length === 0 ? 0 : totalLength / cells.length;
    const maxLength = cells.reduce((max, cell) => Math.max(max, cell.length), 0);

    return {
      index,
      averageLength,
      maxLength,
    };
  });
}

export function getScoutTableLayoutPlan(children?: ReactNode): ScoutTableLayoutPlan {
  const rows = getTableRows(children);
  const metrics = getColumnMetrics(rows);
  const sortedByAverageLength = [...metrics].sort((first, second) => {
    return second.averageLength - first.averageLength;
  });
  const dominantColumn = sortedByAverageLength[0];
  const secondLargestColumn = sortedByAverageLength[1];
  const dominantColumnIndex =
    dominantColumn &&
    secondLargestColumn &&
    dominantColumn.index === metrics.length - 1 &&
    dominantColumn.averageLength >= DOMINANT_COLUMN_MIN_AVERAGE_LENGTH &&
    dominantColumn.averageLength >= secondLargestColumn.averageLength * DOMINANT_COLUMN_RATIO
      ? dominantColumn.index
      : undefined;

  return {
    columnCount: metrics.length,
    shouldScrollHorizontally: metrics.length >= SCROLLABLE_TABLE_MIN_COLUMN_COUNT,
    dominantColumnIndex,
    compactColumnIndexes:
      dominantColumnIndex === undefined
        ? []
        : metrics
            .filter((metric) => metric.index !== dominantColumnIndex)
            .filter((metric) => metric.averageLength <= COMPACT_COLUMN_MAX_AVERAGE_LENGTH)
            .map((metric) => metric.index),
  };
}
